import { Link } from 'react-router-dom';
import { useSyncOptional } from '@/lib/sync-context';
import { useIsDesktop } from '@/lib/native-context';

/**
 * Surfaces outbox entries that have exhausted MAX_ATTEMPTS and won't
 * auto-retry — i.e. real conflicts the user needs to resolve manually.
 *
 * Distinct from `SyncStatusBadge`:
 *   - SyncStatusBadge tracks the active queue (pending + in-flight) and
 *     network state. Always visible on desktop.
 *   - ConflictBadge ONLY appears when failedCount > 0. It's a danger
 *     signal (red) — every failed entry is a piece of user work that
 *     hasn't reached the server. Hidden when zero, so the chrome stays
 *     quiet during normal operation.
 *
 * Click → /settings/sync (the existing sync status page, which we'll
 * grow a dedicated "Conflits" section in).
 */
export function ConflictBadge() {
  const isDesktop = useIsDesktop();
  const sync = useSyncOptional();
  if (!isDesktop || !sync) return null;
  if (sync.failedCount === 0) return null;

  const label = `Conflits · ${sync.failedCount}`;
  const tooltip = `${sync.failedCount} opération(s) à examiner — cliquer pour résoudre`;

  return (
    <Link
      to="/settings/sync"
      title={tooltip}
      aria-label={tooltip}
      aria-live="polite"
      className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-950 dark:text-red-300 dark:hover:bg-red-900"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
      {label}
    </Link>
  );
}
